// Change-level helpers for the epoch rules: the changes a sealed batch carries, a document's changes beyond some
// heads, and heads sets kept as plain arrays of hex hashes.
import * as A from '@automerge/automerge';
import { backendOf, type Backend } from './tree.ts';

const MAGIC = [0x85, 0x6f, 0x4a, 0x83];

/** The chunks of concatenated Automerge changes (a batch), or null when the bytes aren't that. */
export function splitChanges(bytes: Uint8Array): Uint8Array[] | null {
  const out: Uint8Array[] = [];
  let at = 0;
  while (at < bytes.length) {
    if (bytes.length - at < 10 || MAGIC.some((m, i) => bytes[at + i] !== m)) return null;
    let p = at + 9; // magic, checksum, chunk type
    let length = 0, shift = 0;
    for (;;) {
      if (p >= bytes.length) return null;
      const byte = bytes[p++];
      length += (byte & 0x7f) * 2 ** shift;
      shift += 7;
      if (!(byte & 0x80)) break;
      if (shift > 49) return null;
    }
    const end = p + length;
    if (end > bytes.length) return null;
    out.push(bytes.subarray(at, end));
    at = end;
  }
  return out;
}

/** The hashes of the changes in a batch (empty when it can't be read as changes). */
export function changeHashes(bytes: Uint8Array): string[] {
  const chunks = splitChanges(bytes);
  if (!chunks) return [];
  const out: string[] = [];
  for (const c of chunks) {
    try {
      out.push(A.decodeChange(c).hash);
    } catch {
      return [];
    }
  }
  return out;
}

/** Hashes as a sorted, de-duplicated list. */
export const headsList = (heads: Iterable<string>) => [...new Set(heads)].sort();

export const sameHeads = (a: readonly string[], b: readonly string[]) => {
  const x = headsList(a), y = headsList(b);
  return x.length === y.length && x.every((h, i) => h === y[i]);
};

/** Whether the document holds every one of `heads` (and there's at least one). */
export function holds(doc: A.Doc<unknown> | Backend, heads: readonly string[]): boolean {
  if (!heads.length) return false;
  const b = backendOf(doc);
  return heads.every((h) => b.getChangeMetaByHash(h) !== null);
}

/** Change metadata by hash, for walking the graph. */
export interface ChangeInfo {
  hash: string;
  deps: string[];
  actor: string;
  seq: number;
  time: number;
  message: string | null;
}

export function changeInfo(doc: A.Doc<unknown> | Backend, hash: string): ChangeInfo | null {
  const m = backendOf(doc).getChangeMetaByHash(hash);
  return m ? { hash: m.hash, deps: m.deps, actor: m.actor, seq: m.seq, time: m.time, message: m.message } : null;
}

/** Every change of the document that isn't `heads` or an ancestor of them, by hash. */
export function changesBeyond(doc: A.Doc<unknown> | Backend, heads: readonly string[]): Map<string, ChangeInfo> {
  const out = new Map<string, ChangeInfo>();
  for (const m of backendOf(doc).getChangesMeta([...heads])) {
    out.set(m.hash, { hash: m.hash, deps: m.deps, actor: m.actor, seq: m.seq, time: m.time, message: m.message });
  }
  return out;
}
